/** Pure helpers for the chat screens, unit-tested in chatHelpers.test.ts. */
import {
  CHAT_EDIT_WINDOW_MS,
  type ChatChannelSummary,
  type ChatMessage,
  type ChatOverview,
} from '@aroo/shared';

export type Segment =
  | { kind: 'text'; text: string }
  | { kind: 'mention'; text: string }
  | { kind: 'link'; text: string; href: string };

const URL_RE = /\bhttps?:\/\/[^\s<>"']+[^\s<>"'.,;:!?)\]]/gi;

/**
 * Splits a message into plain text, @mentions (of the people the server recognised) and links.
 * Everything is rendered as text nodes, never HTML.
 */
export function segmentBody(body: string, mentionNames: readonly string[]): Segment[] {
  const names = [...new Set(mentionNames)].sort((a, b) => b.length - a.length);
  const out: Segment[] = [];
  const pushText = (text: string) => {
    if (!text) return;
    const last = out.at(-1);
    if (last?.kind === 'text') last.text += text;
    else out.push({ kind: 'text', text });
  };
  const withMentions = (text: string) => {
    if (names.length === 0) return pushText(text);
    const lower = text.toLowerCase();
    let i = 0;
    while (i < text.length) {
      const at = lower.indexOf('@', i);
      if (at === -1) break;
      const hit = names.find((n) => lower.startsWith(n.toLowerCase(), at + 1));
      if (hit) {
        pushText(text.slice(i, at));
        out.push({ kind: 'mention', text: text.slice(at, at + 1 + hit.length) });
        i = at + 1 + hit.length;
      } else {
        pushText(text.slice(i, at + 1));
        i = at + 1;
      }
    }
    pushText(text.slice(i));
  };
  let last = 0;
  for (const m of body.matchAll(URL_RE)) {
    const start = m.index ?? 0;
    withMentions(body.slice(last, start));
    out.push({ kind: 'link', text: m[0], href: m[0] });
    last = start + m[0].length;
  }
  withMentions(body.slice(last));
  return out;
}

/** The @word being typed at the caret: where it starts and what follows the @. */
export function mentionQuery(text: string, caret: number): { start: number; query: string } | null {
  const before = text.slice(0, caret);
  const at = before.lastIndexOf('@');
  if (at === -1) return null;
  if (at > 0 && !/\s|\(/.test(before[at - 1])) return null;
  const query = before.slice(at + 1);
  if (query.length > 30 || /\n/.test(query) || /\s{2}/.test(query)) return null;
  return { start: at, query };
}

/** Replaces the @query at `start..caret` with "@Full Name ". */
export function applyMention(
  text: string,
  start: number,
  caret: number,
  name: string,
): { text: string; caret: number } {
  const insert = `@${name} `;
  return { text: text.slice(0, start) + insert + text.slice(caret), caret: start + insert.length };
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const localDay = (d: Date) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;

export function dayLabel(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  if (localDay(d) === localDay(now)) return 'Today';
  const y = new Date(now);
  y.setDate(now.getDate() - 1);
  if (localDay(d) === localDay(y)) return 'Yesterday';
  return `${d.getDate()} ${MONTHS[d.getMonth()]}${d.getFullYear() === now.getFullYear() ? '' : ` ${d.getFullYear()}`}`;
}

export function timeLabel(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** Short "when" for lists: time today, else day label. */
export function whenLabel(iso: string | null, now: Date = new Date()): string {
  if (!iso) return '';
  const day = dayLabel(iso, now);
  return day === 'Today' ? timeLabel(iso) : day;
}

/** Live frames carry no per-viewer flags; the author's own copy is editable for 15 minutes. */
export function canEditNow(m: ChatMessage, me: string, now: Date = new Date()): boolean {
  return (
    m.kind === 'text' &&
    !m.deleted &&
    m.author?.id === me &&
    now.getTime() - new Date(m.createdAt).getTime() <= CHAT_EDIT_WINDOW_MS
  );
}

export function canDeleteNow(m: ChatMessage, me: string): boolean {
  return m.kind === 'text' && !m.deleted && m.author?.id === me;
}

/** A moderator may remove any text message that is still there (D73). */
export function canModerateNow(m: ChatMessage, canModerate: boolean): boolean {
  return canModerate && m.kind === 'text' && !m.deleted;
}

/** What a deleted message shows in place of its text. */
export function deletedLabel(m: Pick<ChatMessage, 'removedByModerator'>): string {
  return m.removedByModerator ? 'Removed by a moderator' : 'Message deleted';
}

/** Most recent conversations across channels, DMs and threads (for the header popover). */
export function recentConversations(o: ChatOverview | undefined, limit = 8): ChatChannelSummary[] {
  if (!o) return [];
  return [...o.channels, ...o.dms, ...o.threads]
    .filter((c) => c.lastMessageAt || c.unread > 0)
    .sort(
      (a, b) =>
        Number(b.unread > 0) - Number(a.unread > 0) ||
        (b.lastMessageAt ?? '').localeCompare(a.lastMessageAt ?? ''),
    )
    .slice(0, limit);
}
